import React, { useState } from 'react'
import { lmsAdmin } from '../api'
import { LmsIcon } from '../lms-icon'
import { ScoreReportingFields, describeScoring, draftError, draftOf, scoringOf } from '../score-reporting'

// "How the course score and pass are reported" for one course on one LMS
// connection (LMS Phase F scoring): the course follows its connection's
// preset, or has its own setting. Saving sends PATCH
// /admin/lms/connections/{id}/courses/{nodeId} {scoring} (null: follow the
// preset); the LMS admin scope only, which the service checks.

export const CourseScoringDialog = ({ connection, nodeId, mapping, onClose, onSaved }) => {
  const own = mapping.scoring || null
  const [follow, updateFollow] = useState(!own)
  const [draft, updateDraft] = useState(draftOf(own || mapping.effective_scoring))
  const [markError, updateMarkError] = useState(null)
  const [error, updateError] = useState(null)
  const [busy, updateBusy] = useState(false)

  // What "Use the preset's setting" would apply: the preset's own setting
  // (preset_scoring), also while the course has its own. An older API answer
  // has none: a course that follows the preset then has the preset's as its
  // effective setting; one with its own gets the generic sentence.
  const presetSetting = mapping.preset_scoring || (own ? null : mapping.effective_scoring)
  const presetDescription = presetSetting
    ? describeScoring(presetSetting)
    : 'The setting of this connection’s preset, for every course without its own.'

  const fieldErrors = (error && error.details && error.details.fields) || {}
  const shownFields = ['scoring.pass_rule', 'scoring.score_rule', 'scoring.pass_mark']
  const bannerError = error && (Object.keys(fieldErrors).length === 0 || Object.keys(fieldErrors).some(f => !shownFields.includes(f)))

  const save = async () => {
    if (busy) return
    if (!follow) {
      const invalid = draftError(draft)
      updateMarkError(invalid)
      if (invalid) return
    }
    updateBusy(true)
    updateError(null)
    try {
      await lmsAdmin.setCourseScoring(connection.id, Number(nodeId), follow ? null : scoringOf(draft))
      onSaved()
    } catch (e) {
      updateError(e)
      updateBusy(false)
    }
  }

  return (
    <div className="lms-modal-scrim" data-testid="course-scoring-dialog">
      <div className="lms-modal">
        <div className="modal-head">
          <div className="icon">
            <LmsIcon name="list-check" />
          </div>
          <div className="body">
            <h2>How the course score and pass are reported</h2>
            <p className="target">
              on <span className="target-name">{connection.name}</span>
            </p>
          </div>
          <button className="close-btn" title="Close" type="button" onClick={onClose}>
            <LmsIcon name="xmark" />
          </button>
        </div>

        <div className="lms-modal-body">
          {bannerError && (
            <div className="alert alert-danger" role="alert" data-testid="course-scoring-error">
              {error.message}
            </div>
          )}
          <div className="radio-group mb-3">
            <label className={`radio-option ${follow ? 'selected' : ''}`}>
              <input
                type="radio"
                name="course-scoring-source"
                checked={follow}
                data-testid="course-scoring-follow"
                onChange={() => updateFollow(true)}
              />
              <div className="body">
                <div className="name">Use the preset&apos;s setting</div>
                <div className="desc" data-testid="course-scoring-follow-desc">
                  {presetDescription}
                </div>
              </div>
            </label>
            <label className={`radio-option ${!follow ? 'selected' : ''}`}>
              <input
                type="radio"
                name="course-scoring-source"
                checked={!follow}
                data-testid="course-scoring-own"
                onChange={() => updateFollow(false)}
              />
              <div className="body">
                <div className="name">Set it for this course</div>
                <div className="desc">This course only, on this connection.</div>
              </div>
            </label>
          </div>
          {!follow && (
            <ScoreReportingFields
              name="course-scoring"
              draft={draft}
              onChange={next => {
                updateDraft(next)
                updateMarkError(null)
              }}
              errors={{
                pass_rule: fieldErrors['scoring.pass_rule'],
                score_rule: fieldErrors['scoring.score_rule'],
                pass_mark: markError || fieldErrors['scoring.pass_mark']
              }}
            />
          )}
        </div>

        <div className="modal-foot">
          <button className="btn btn-light border btn-sm" type="button" style={{ marginLeft: 'auto' }} onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary btn-sm" type="button" disabled={busy} data-testid="course-scoring-save" onClick={save}>
            Save
          </button>
        </div>
      </div>
    </div>
  )
}
